import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import userActions from "../../../_actions/userActions";
import unitActions from "../../../_actions/unitActions";
import AlertModal from "../../_common/AlertModal";
import Select from "react-select";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { modalTabIds } from "../../../_constants/modalTabIds";

// For editing existing user data; NOT for adding new user.
// (Different from "registration", which really is an update, not add)
export default function EditUserDetailsModal(props) {
  const {
    showModal,
    toggleModal,
    user,
    isNewUserRegistration,
    tabId,
    allowRegisterAsOfficeAdmin,
  } = props;

  const dispatch = useDispatch();

  const currentUserId = useSelector((state) => state.authentication.user?.userId ?? null);
  const userRoles = useSelector((state) => state.users.userRoles || null);
  const units = useSelector(
    (state) => state.users.openUnitLists[modalTabIds.EDIT_USER_DETAILS] || null
  );

  useEffect(() => {
    dispatch(userActions.getUserRoles());
    dispatch(unitActions.getAll(null, modalTabIds.EDIT_USER_DETAILS));
    validateForm();
  }, []);

  const [_userData, setUserData] = useState(user ? { ...user } : {});
  useEffect(() => setUserData(user ? { ...user } : {}), [user]);

  const unitOptions = useMemo(
    () =>
      _.orderBy(
        (units || []).map((u) => ({
          value: u.unitId,
          label: u.distUnit,
          district: u.district,
        })),
        "label",
        "asc"
      ),
    [units]
  );

  const userRoleOptions = useMemo(() => {
    const userRoleOrder = {
      CONSULT: 0,
      TET: 1,
      TEC: 2,
      TEE: 3,
      TEL: 4,
      STE: 5,
      SBE: 6,
      STEE: 7,
      SUPTE: 8,
      SUPBE: 9,
      AGPA: 10,
      SSM: 11,
    };
    return _.orderBy(
      (userRoles || [])
        .filter((ur) => ur.userRoleSymbol !== "OA")
        .map((ur) => ({
          value: ur.userRoleId,
          label: ur.description,
          order: userRoleOrder[ur.userRoleSymbol],
          userRoleSymbol: ur.userRoleSymbol,
        })),
      "order",
      "asc"
    );
  }, [userRoles]);

  const [showAlert_editUserConfirm, setShowAlert_editUserConfirm] = useState(false);
  const toggleAlert_editUserConfirm = () =>
    setShowAlert_editUserConfirm(!showAlert_editUserConfirm);

  const [firstName, setFirstName] = useState(user ? user.firstName || "" : "");
  const [middleInitial, setMiddleInitial] = useState(
    user ? user.middleInitial || "" : ""
  );
  const [lastName, setLastName] = useState(user ? user.lastName || "" : "");
  const [employeeNo, setEmployeeNo] = useState(user ? user.employeeNo : "");
  const [phoneNumber, setPhoneNumber] = useState(user ? user.phoneNumber : "");
  const [email, setEmail] = useState(user ? user.email : "");
  const [consultantCompanyName, setConsultantCompanyName] = useState(
    user ? user.consultantCompanyName : ""
  );

  const [selectedUserRole, setSelectedUserRole] = useState(
    user ? (userRoleOptions || []).find((uro) => uro.value === user.userRoleId) : null
  );
  const [selectedUnit, setSelectedUnit] = useState(
    user ? (unitOptions || []).find((uo) => uo.value === user.unitId) : null
  );
  const [isDistrictAdmin, setIsDistrictAdmin] = useState(
    user ? user.isOfficeAdmin : false
  );

  const [formIsIncomplete, setFormIsIncomplete] = useState(false);
  const [formErrors, setFormErrors] = useState({
    firstName: null,
    lastName: null,
    userRole: null,
    employeeNo: null,
  });

  useEffect(
    () => setFormIsIncomplete(Object.values(formErrors).some((e) => e)),
    [formErrors]
  );

  const validateForm = useCallback(() => {
    const standardError = "Required";

    const _formErrors = {
      ...formErrors,
      firstName: firstName ? null : standardError,
      lastName: lastName ? null : standardError,
      userRole: selectedUserRole ? null : standardError,
      employeeNo: employeeNo ? null : standardError,
    };
    setFormErrors(_formErrors);
  }, [firstName, lastName, selectedUserRole, employeeNo]);

  useEffect(() => validateForm(), [employeeNo, firstName, lastName, selectedUserRole]);

  const handleBlur = (fieldName, value) => {
    validateForm();
    setUserData({ ..._userData, [fieldName]: value });
  };

  const handleChange_userRole = (selectedOption) => {
    setSelectedUserRole(selectedOption);
    setUserData({ ..._userData, userRoleId: selectedOption?.value });
    validateForm();
  };

  const handleChange_unit = (selectedOption) => {
    setSelectedUnit(selectedOption);
    setUserData({ ..._userData, unitId: selectedOption?.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!user || formIsIncomplete) return;

    const userData = {
      userId: user.userId,
      employeeNo,
      firstName,
      middleInitial,
      lastName,
      phoneNumber,
      email,
      consultantCompanyName,
      userRoleId: selectedUserRole?.value,
      unitId: selectedUnit?.value,
      isOfficeAdmin: isDistrictAdmin,
    };

    // Update existing user info
    dispatch(userActions.update(userData, currentUserId));
    toggleModal();
  };

  const renderInputs = () => {
    return (
      <div className="card flex-grow-1">
        <div className="card-body">
          <div className="row">
            <div className="form-group col-7 px-2">
              <label className="mb-0" htmlFor="employeeNo">
                <b>Employee No.:</b>
              </label>
              <input
                type="text"
                className="form-control font-weight-bold"
                name="employeeNo"
                id="employeeNo"
                maxLength="7"
                value={employeeNo}
                onChange={() => {}}
                onBlur={() => {}}
                disabled={true}
              />
            </div>
          </div>
          <div className="row">
            <div className="form-group col-5 px-2">
              <label className="mb-0" htmlFor="firstName">
                <b>First Name:</b>*
              </label>
              <input
                type="text"
                className="form-control"
                name="firstName"
                id="firstName"
                value={firstName || ""}
                onChange={(e) => setFirstName(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
              />
              {formErrors.firstName && (
                <span className="error-small">
                  <small>{formErrors.firstName}</small>
                </span>
              )}
            </div>
            <div className="form-group col-2 px-2">
              <label className="mb-0" htmlFor="middleInitial">
                MI:
              </label>
              <input
                type="text"
                className="form-control"
                name="middleInitial"
                id="middleInitial"
                maxLength="1"
                value={middleInitial || ""}
                onChange={(e) => setMiddleInitial(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
              />
            </div>
            <div className="form-group col-5 px-2">
              <label className="mb-0" htmlFor="lastName">
                <b>Last Name:</b>*
              </label>
              <input
                type="text"
                className="form-control"
                name="lastName"
                id="lastName"
                value={lastName || ""}
                onChange={(e) => setLastName(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
              />
              {formErrors.lastName && (
                <span className="error-small">
                  <small>{formErrors.lastName}</small>
                </span>
              )}
            </div>
          </div>
          <div className="row">
            <div className="form-group col-6 px-2">
              <label className="mb-0" htmlFor="phoneNumber">
                Phone Number:
              </label>
              <input
                type="text"
                className="form-control"
                name="phoneNumber"
                id="phoneNumber"
                value={phoneNumber || ""}
                onChange={(e) => setPhoneNumber(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
              />
            </div>
            <div className="form-group col-6 px-2">
              <label className="mb-0" htmlFor="email">
                Email:
              </label>
              <input
                type="text"
                className="form-control"
                name="email"
                id="email"
                value={email || ""}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
              />
            </div>
          </div>
          <div className="row">
            <div className="form-group col px-2">
              <label className="mb-1" htmlFor="district">
                <b>Classification:</b>*
              </label>
              <Select
                name="selectedUserRole"
                id="selectedUserRole"
                value={selectedUserRole}
                isSearchable={false}
                options={userRoleOptions}
                onChange={(selectedOption) => handleChange_userRole(selectedOption)}
                isClearable
                placeholder="Select Classification"
                styles={{
                  // Fixes the overlapping problem of the component
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
              {formErrors.userRole && (
                <span className="error-small">
                  <small>{formErrors.userRole}</small>
                </span>
              )}
            </div>
          </div>
          {selectedUserRole && selectedUserRole.userRoleSymbol === "CONSULT" && (
            <div className="row">
              <div className="form-group col-6 px-2">
                <label className="mb-0" htmlFor="consultantCompanyName">
                  Consultant Name (Company):
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="consultantCompanyName"
                  id="consultantCompanyName"
                  value={consultantCompanyName || ""}
                  onChange={(e) => setConsultantCompanyName(e.target.value)}
                  onBlur={(e) => handleBlur(e.target.name, e.target.value)}
                />
              </div>
            </div>
          )}
          <div className="row">
            <div className="form-group col px-2">
              <label className="mb-1" htmlFor="district">
                District/Unit:
              </label>
              <Select
                name="selectedUnit"
                id="selectedUnit"
                value={selectedUnit}
                options={unitOptions}
                onChange={(selectedOption) => handleChange_unit(selectedOption)}
                isClearable
                styles={{
                  // Fixes the overlapping problem of the component
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
            </div>
          </div>
          {allowRegisterAsOfficeAdmin && (
            <div className="row">
              <div className="form-group col mb-0 px-2">
                <div className="custom-control custom-checkbox">
                  <input
                    type="checkbox"
                    name="isDistrictAdmin"
                    id="isDistrictAdmin"
                    className="custom-control-input"
                    checked={isDistrictAdmin}
                    onChange={(e) => setIsDistrictAdmin(e.target.checked)}
                  />
                  <label className="custom-control-label mb-1" htmlFor="isDistrictAdmin">
                    Register as District Administrator
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const closeBtn = (
    <button className="close" onClick={toggleModal}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  return (
    <React.Fragment>
      <Modal
        isOpen={showModal}
        toggle={toggleModal}
        className={props.className}
        fade={false}
        size="lg"
      >
        <ModalHeader className="bg-info text-white" close={closeBtn}>
          <FontAwesomeIcon
            icon={isNewUserRegistration ? "plus" : "edit"}
            className="fa-fw mr-2"
          />
          {isNewUserRegistration ? "User Registration" : "Edit User Information"}
        </ModalHeader>
        <ModalBody>{renderInputs()}</ModalBody>
        <ModalFooter className="bg-lightgray">
          <div className="form-group mb-0">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={formIsIncomplete}
            >
              Save
            </button>
            <button
              type="button"
              className="btn btn-secondary ml-2"
              name="cancelEditUser"
              onClick={toggleModal}
            >
              Cancel
            </button>
          </div>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
}
